import React, { useState, useEffect } from 'react';
import { Modal } from '../../shared/components/Modal';
import { Button } from '../../shared/components/Button';
import { TableBlock, TableCell } from '../ast/document.types';
import { Plus, Trash2, Check } from 'lucide-react';

interface TableEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  tableBlock: TableBlock | null;
  sectionId: string | null;
  onSave: (sectionId: string, blockId: string, updates: Partial<TableBlock['data']>) => void;
}

export const TableEditModal: React.FC<TableEditModalProps> = ({
  isOpen,
  onClose,
  tableBlock,
  sectionId,
  onSave,
}) => {
  const [cells, setCells] = useState<TableCell[][]>([]);
  const [caption, setCaption] = useState('');
  const [hasHeaderRow, setHasHeaderRow] = useState(true);
  const [borderStyle, setBorderStyle] = useState<'grid' | 'academic' | 'minimal' | 'none'>('academic');

  useEffect(() => {
    if (tableBlock) {
      setCells(JSON.parse(JSON.stringify(tableBlock.data.cells || [])));
      setCaption(tableBlock.data.caption || '');
      setHasHeaderRow(tableBlock.data.hasHeaderRow !== false);
      setBorderStyle(tableBlock.data.borderStyle || 'academic');
    }
  }, [tableBlock]);

  if (!tableBlock || !sectionId) return null;

  const handleCellChange = (rIdx: number, cIdx: number, val: string) => {
    setCells((prev) => {
      const next = prev.map((row) => [...row]);
      if (next[rIdx] && next[rIdx][cIdx]) {
        next[rIdx][cIdx] = { ...next[rIdx][cIdx], content: val };
      }
      return next;
    });
  };

  const handleAddRow = () => {
    setCells((prev) => {
      const numCols = prev[0]?.length || 3;
      const newRow: TableCell[] = Array.from({ length: numCols }, (_, idx) => ({
        id: `c-${Date.now()}-${idx}`,
        content: '',
        alignment: 'center',
      }));
      return [...prev, newRow];
    });
  };

  const handleAddCol = () => {
    setCells((prev) => {
      return prev.map((row, rIdx) => [
        ...row,
        {
          id: `c-${Date.now()}-${rIdx}`,
          content: rIdx === 0 && hasHeaderRow ? `Header ${row.length + 1}` : '',
          isHeader: rIdx === 0 && hasHeaderRow,
          alignment: 'center',
        },
      ]);
    });
  };

  const handleRemoveRow = (rIdx: number) => {
    if (cells.length <= 1) return;
    setCells((prev) => prev.filter((_, idx) => idx !== rIdx));
  };

  const handleRemoveCol = (cIdx: number) => {
    if (cells[0]?.length <= 1) return;
    setCells((prev) => prev.map((row) => row.filter((_, idx) => idx !== cIdx)));
  };

  const handleSave = () => {
    onSave(sectionId, tableBlock.id, {
      cells,
      rows: cells.length,
      cols: cells[0]?.length || 0,
      caption,
      hasHeaderRow,
      borderStyle,
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Table Grid & Data"
      maxWidth="760px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" icon={<Check size={16} />} onClick={handleSave}>
            Apply Changes
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {/* Caption & Style */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-3)' }}>
          <div>
            <label className="form-label">Table Caption</label>
            <input
              type="text"
              className="form-input"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="e.g. Table 1: Forward Bias Diode Measurements"
            />
          </div>

          <div>
            <label className="form-label">Border Style</label>
            <select
              className="form-select"
              value={borderStyle}
              onChange={(e) => setBorderStyle(e.target.value as any)}
            >
              <option value="academic">Academic (APA standard)</option>
              <option value="grid">Full Grid</option>
              <option value="minimal">Minimal Underlines</option>
              <option value="none">No Borders</option>
            </select>
          </div>
        </div>

        {/* Toolbar to Add Row / Col */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button variant="secondary" size="sm" icon={<Plus size={14} />} onClick={handleAddRow}>
            Add Row
          </Button>
          <Button variant="secondary" size="sm" icon={<Plus size={14} />} onClick={handleAddCol}>
            Add Column
          </Button>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', marginLeft: 'auto', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={hasHeaderRow}
              onChange={(e) => setHasHeaderRow(e.target.checked)}
            />
            <span>Header Row Enabled</span>
          </label>
        </div>

        {/* Interactive Cell Grid */}
        <div style={{ maxHeight: '380px', overflow: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              {hasHeaderRow && cells.length > 0 && (
                <tr style={{ backgroundColor: '#f1f5f9' }}>
                  {cells[0].map((cell, cIdx) => (
                    <th key={cIdx} style={{ padding: '6px', border: '1px solid var(--border-subtle)', minWidth: '100px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input
                          type="text"
                          className="form-input text-xs"
                          style={{ fontWeight: 700, textAlign: 'center' }}
                          value={cell.content}
                          onChange={(e) => handleCellChange(0, cIdx, e.target.value)}
                        />
                        <button
                          type="button"
                          className="icon-btn"
                          style={{ width: 20, height: 20, color: 'var(--color-error)' }}
                          onClick={() => handleRemoveCol(cIdx)}
                          title="Delete Column"
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </th>
                  ))}
                  <th style={{ width: '40px' }} />
                </tr>
              )}
            </thead>
            <tbody>
              {cells.slice(hasHeaderRow ? 1 : 0).map((row, rOffset) => {
                const rIdx = hasHeaderRow ? rOffset + 1 : rOffset;
                return (
                  <tr key={rIdx}>
                    {row.map((cell, cIdx) => (
                      <td key={cIdx} style={{ padding: '4px', border: '1px solid var(--border-subtle)' }}>
                        <input
                          type="text"
                          className="form-input text-xs"
                          value={cell.content}
                          onChange={(e) => handleCellChange(rIdx, cIdx, e.target.value)}
                          placeholder="—"
                        />
                      </td>
                    ))}
                    <td style={{ padding: '4px', textAlign: 'center', width: '40px' }}>
                      <button
                        type="button"
                        className="icon-btn"
                        style={{ width: 22, height: 22, color: 'var(--color-error)' }}
                        onClick={() => handleRemoveRow(rIdx)}
                        title="Delete Row"
                      >
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
};
